import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiHeart, FiChevronDown, FiGrid } from 'react-icons/fi';
import { translations } from '../translations';

const Sidebar = ({
  activeCategory,
  setActiveCategory,
  categories,
  isOpen,
  onClose,
  lang,
  onShowPricing,
  showPricing,
  onLogoClick
}) => {
  const t = translations[lang];
  const [expandedGroups, setExpandedGroups] = useState({
    discovery: true,
    business: false,
    lifestyle: false,
    elements: false
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const renderGroup = (groupId, label) => {
    const groupItems = categories.filter((category) => category.group === groupId);
    if (groupItems.length === 0) return null;

    const isExpanded = expandedGroups[groupId];

    return (
      <div key={groupId} className="py-2 first:pt-0">
        <button
          onClick={() => toggleGroup(groupId)}
          className="w-full px-4 mb-2 py-2 flex items-center justify-between group/title"
        >
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] group-hover/title:text-indigo-500 transition-colors">
            {label}
          </span>
          <SafeIcon
            icon={FiChevronDown}
            className={`text-[10px] text-slate-300 transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="space-y-0.5 pb-3">
                {groupItems.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      if (window.innerWidth < 768) onClose();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                      activeCategory === category.id && !showPricing
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100 font-bold'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg transition-colors ${
                        activeCategory === category.id && !showPricing
                          ? 'bg-white/20'
                          : 'bg-slate-100 group-hover:bg-white'
                      }`}
                    >
                      <SafeIcon icon={category.icon} className="text-sm" />
                    </div>
                    <span className="flex-1 text-left text-xs whitespace-nowrap truncate">
                      {t.categories[category.id] || category.name}
                    </span>
                    {activeCategory === category.id && !showPricing && (
                      <motion.div
                        layoutId="activeDot"
                        className="w-1.5 h-1.5 rounded-full bg-white shadow-sm"
                      />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white">
      <div className="p-6 flex flex-col gap-1 border-b border-slate-50">
        <div className="flex items-center justify-between">
          <button
            onClick={onLogoClick}
            className="flex items-center gap-2 group/logo hover:opacity-80 transition-all active:scale-95"
            aria-label={lang === 'zh-TW' ? '返回 Emoji Glint 首頁' : 'Return to Emoji Glint home'}
          >
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center p-1.5 shadow-lg group-hover/logo:shadow-indigo-200 transition-all">
              <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
                <path
                  d="M50 10 L58 42 L90 50 L58 58 L50 90 L42 58 L10 50 L42 42 Z"
                  fill="white"
                />
              </svg>
            </div>
            <span className="text-xl font-black text-slate-800 tracking-tighter leading-none group-hover/logo:text-indigo-600 transition-colors">
              {t.title}
            </span>
          </button>
          <button
            onClick={onClose}
            className="md:hidden p-2 hover:bg-slate-100 rounded-full transition-colors"
            aria-label={lang === 'zh-TW' ? '關閉選單' : 'Close menu'}
          >
            <SafeIcon icon={FiX} className="text-xl text-slate-400" />
          </button>
        </div>
        <p className="text-slate-400 text-[9px] font-bold mt-2 uppercase tracking-[0.2em] pl-0.5">
          {t.subtitle}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-5 custom-scrollbar">
        <div className="mb-6">
          <button
            onClick={() => {
              setActiveCategory('all');
              if (window.innerWidth < 768) onClose();
            }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 group ${
              activeCategory === 'all' && !showPricing
                ? 'bg-slate-900 text-white shadow-xl shadow-slate-200 font-black'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-100'
            }`}
          >
            <div
              className={`p-1.5 rounded-lg transition-colors ${
                activeCategory === 'all' && !showPricing
                  ? 'bg-white/20'
                  : 'bg-white shadow-sm'
              }`}
            >
              <SafeIcon icon={FiGrid} className="text-sm" />
            </div>
            <span className="flex-1 text-left text-xs uppercase tracking-widest">
              {t.categories.all}
            </span>
            {activeCategory === 'all' && !showPricing && (
              <motion.div
                layoutId="activeDot"
                className="w-1.5 h-1.5 rounded-full bg-white shadow-sm"
              />
            )}
          </button>
        </div>

        <div className="divide-y divide-slate-100/50">
          {renderGroup(
            'discovery',
            lang === 'zh-TW' ? '🚀 最新趨勢' : 'Discovery'
          )}
          {renderGroup(
            'business',
            lang === 'zh-TW' ? '💼 專業行銷' : 'Business'
          )}
          {renderGroup(
            'lifestyle',
            lang === 'zh-TW' ? '🎨 生活文化' : 'Lifestyle'
          )}
          {renderGroup(
            'elements',
            lang === 'zh-TW' ? '🧩 基礎萬用' : 'The Basics'
          )}
        </div>
      </div>

      <div className="p-3 border-t border-slate-100 bg-slate-50/50">
        <button
          onClick={() => {
            onShowPricing();
            if (window.innerWidth < 768) onClose();
          }}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group ${
            showPricing
              ? 'bg-rose-500 text-white shadow-md shadow-rose-100 font-bold'
              : 'text-slate-500 hover:bg-rose-50 hover:text-rose-600 border-2 border-rose-100 hover:border-rose-200'
          }`}
        >
          <div
            className={`p-1.5 rounded-lg transition-colors ${
              showPricing
                ? 'bg-white/20'
                : 'bg-rose-100 group-hover:bg-rose-200'
            }`}
          >
            <SafeIcon icon={FiHeart} className="text-sm" />
          </div>
          <span className="flex-1 text-left text-xs font-bold">
            {t.supportUs}
          </span>
          {showPricing && (
            <motion.div
              layoutId="activeDot"
              className="w-1.5 h-1.5 rounded-full bg-white shadow-sm"
            />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden md:block w-56 border-r border-slate-100 h-screen sticky top-0 shrink-0 z-40">
        {sidebarContent}
      </aside>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 md:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-[260px] bg-white z-[60] md:hidden shadow-2xl"
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;